import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import SearchIcon from '@mui/icons-material/Search'
import { CircularProgress } from '@mui/joy'
import axios from 'axios'
import { formatToUserLocalTime } from '@/utils/date-formatting'

interface EtaHistoryRow {
    projectName: string
    assignedLoad: string
    truckMainInfo: string
    deliveryStatus: string
    stopLat: number
    stopLng: number
    eta: string
    durationHours: number | null
    provider: string
    providerResponse: string
    timestamp: string
}

const PAGE_SIZE = 100

export default function EtaHistory() {
    const [rows, setRows] = React.useState<EtaHistoryRow[]>([])
    const [page, setPage] = React.useState(1)
    const [loading, setLoading] = React.useState(true)
    const [fetching, setFetching] = React.useState(false)
    const [totalCount, setTotalCount] = React.useState(0)
    const [totalPages, setTotalPages] = React.useState(0)

    const [projectFilter, setProjectFilter] = React.useState('')
    const [assignedLoadFilter, setAssignedLoadFilter] = React.useState('')
    const [appliedProjectFilter, setAppliedProjectFilter] = React.useState('')
    const [appliedAssignedLoadFilter, setAppliedAssignedLoadFilter] = React.useState('')

    const handleFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            setPage(1)
            setAppliedProjectFilter(projectFilter)
            setAppliedAssignedLoadFilter(assignedLoadFilter)
        }
    }

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const isInitialLoad = page === 1 && !appliedProjectFilter && !appliedAssignedLoadFilter

                if (isInitialLoad) {
                    setLoading(true)
                } else {
                    setFetching(true)
                }

                const params = new URLSearchParams()
                params.append('page', page.toString())
                params.append('pageSize', PAGE_SIZE.toString())
                if (appliedProjectFilter) params.append('projectName', appliedProjectFilter)
                if (appliedAssignedLoadFilter) params.append('assignedLoad', appliedAssignedLoadFilter)

                const res = await axios.get(`gluon/eta-history?${params.toString()}`)
                const data = res.data || {}
                setRows(Array.isArray(data.rows) ? data.rows : [])
                setTotalCount(typeof data.total === 'number' ? data.total : 0)
                setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0)
            } catch (e) {
                console.error('Error fetching ETA history:', e)
                setRows([])
                setTotalCount(0)
                setTotalPages(0)
            } finally {
                setLoading(false)
                setFetching(false)
            }
        }

        fetchData()
    }, [page, appliedProjectFilter, appliedAssignedLoadFilter])

    React.useEffect(() => {
        if (page > totalPages && totalPages > 0) {
            setPage(1)
        }
    }, [totalPages, page])

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography>Loading...</Typography>
            </Box>
        )
    }

    return (
        <React.Fragment>
            <Box
                sx={{
                    display: 'flex',
                    my: 2,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography level="h2" component="h1">
                        ETA History ({totalCount})
                    </Typography>
                    {fetching && <CircularProgress size="sm" />}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="sm" variant="outlined" disabled={page === 1 || fetching} onClick={() => setPage(page - 1)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={page === totalPages || fetching} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: { xs: 'none', sm: 'flex' },
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        width: '100%',
                        alignItems: 'flex-end',
                    }}
                >
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Project Name</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search by project name..."
                            startDecorator={<SearchIcon />}
                            value={projectFilter}
                            onChange={(e) => setProjectFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Assigned Load</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search by assigned load..."
                            startDecorator={<SearchIcon />}
                            value={assignedLoadFilter}
                            onChange={(e) => setAssignedLoadFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                </Box>
            </Box>

            <Sheet
                variant="outlined"
                sx={{
                    display: { xs: 'none', sm: 'initial' },
                    width: '100%',
                    borderRadius: 'sm',
                    flexShrink: 1,
                    overflow: 'auto',
                    minHeight: 0,
                }}
            >
                <Table
                    aria-labelledby="eta-history-table-title"
                    stickyHeader
                    hoverRow
                    sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                        '--TableCell-paddingY': '4px',
                        '--TableCell-paddingX': '8px',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 150, padding: '12px 6px' }}>Project</th>
                            <th style={{ width: 150, padding: '12px 6px' }}>Assigned Load</th>
                            <th style={{ width: 120, padding: '12px 6px' }}>Truck</th>
                            <th style={{ width: 150, padding: '12px 6px' }}>Delivery Status</th>
                            <th style={{ width: 100, padding: '12px 6px' }}>Stop Lat</th>
                            <th style={{ width: 100, padding: '12px 6px' }}>Stop Lng</th>
                            <th style={{ width: 160, padding: '12px 6px' }}>ETA</th>
                            <th style={{ width: 100, padding: '12px 6px' }}>Duration (hrs)</th>
                            <th style={{ width: 100, padding: '12px 6px' }}>Provider</th>
                            <th style={{ width: 200, padding: '12px 6px' }}>Provider Response</th>
                            <th style={{ width: 160, padding: '12px 6px' }}>Timestamp</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={`${row.projectName}-${row.assignedLoad}-${index}`}>
                                <td>
                                    <Typography level="body-xs">{row.projectName}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.assignedLoad}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.truckMainInfo}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.deliveryStatus}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.stopLat.toFixed(4)}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.stopLng.toFixed(4)}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.eta ? formatToUserLocalTime(row.eta) : 'N/A'}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.durationHours !== null ? row.durationHours.toFixed(2) : 'N/A'}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.provider}</Typography>
                                </td>
                                <td>
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            maxWidth: 200,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer',
                                        }}
                                        title={row.providerResponse}
                                    >
                                        {row.providerResponse}
                                    </Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.timestamp ? formatToUserLocalTime(row.timestamp) : 'N/A'}</Typography>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={11} style={{ textAlign: 'center', padding: '16px' }}>
                                    <Typography level="body-sm">No data available</Typography>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Sheet>
        </React.Fragment>
    )
}
